import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import { type GridColDef, type GridFilterItem, type GridFilterOperator, getGridStringOperators } from '@mui/x-data-grid-premium'
import { DeviceType, Status, WalletProvider } from '@mynt/backoffice-api-spec/card-wallet/models'
import { useRef } from 'react'
import { DataGridCellText } from './DataGridCellText'

export type CardTokenRow = {
  id: string
  walletProvider: WalletProvider
  status: Status
  walletProviderName: string
  deviceName?: string
  deviceType?: DeviceType
  mobileNumberLast4?: string
}

export const formatTokenEnum = (val?: string): string => {
  if (!val) return '-'
  const clean = val.replace(/_/g, ' ').toLowerCase()
  return clean.charAt(0).toUpperCase() + clean.slice(1)
}

const createSelectOperator = (label: string, options: string[], getOptionLabel?: (option: string) => string): GridFilterOperator => {
  const sortedOptions = [...options].sort((a, b) => {
    const labelA = getOptionLabel ? getOptionLabel(a) : a
    const labelB = getOptionLabel ? getOptionLabel(b) : b
    return labelA.localeCompare(labelB)
  })

  return {
    label,
    value: 'customArrayContains',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value || filterItem.value.length === 0) return null
      const filterValues = Array.from(filterItem.value)
      return (value: unknown) => filterValues.includes(value)
    },
    InputComponent: ({ applyValue, clearButton, ...params }) => {
      const selected = (params.item.value as string[] | undefined) ?? []
      const labelId = `card-token-filter-${params.item.field}`

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>
          <FormControl fullWidth sx={{ minWidth: 0 }}>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
              labelId={labelId}
              label={label}
              multiple
              value={selected}
              onKeyDown={(event) => event.stopPropagation()}
              onChange={(event) => {
                const value = event.target.value
                applyValue({ ...params.item, value: typeof value === 'string' ? value.split(',') : value })
              }}
              renderValue={(values) => (values.length === 0 ? '' : `${values.length} selected`)}
              sx={{
                // Theme sets `.MuiSelect-multiple` to 8px padding, which is shorter than the
                // 12.5px outlined inputs and drops the label and value below the rest of the row.
                '&& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12.5px 32px 12.5px 12.5px',
                },
              }}
            >
              {sortedOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={selected.includes(option)} size="small" sx={{ pointerEvents: 'none', padding: 0, marginRight: 1 }} />
                  {getOptionLabel ? getOptionLabel(option) : option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selected.length > 0 ? clearButton : null}
        </Box>
      )
    },
    headerLabel: label,
  }
}

// The default `contains` filter renders its input at `size="small"`, which is shorter than
// the header filter row's other controls (see `createSelectOperator` above) and looks clipped
// alongside them. Reuse the default filter logic, just with a full-size, height-matched input.
// getGridStringOperators()[0] is always the 'contains' operator.
const containsFilterFn = getGridStringOperators()[0].getApplyFilterFn

const createTextFilterOperator = (label: string): GridFilterOperator => ({
  label,
  value: 'contains',
  getApplyFilterFn: containsFilterFn,
  InputComponent: ({ applyValue, ...params }) => {
    const timerRef = useRef<ReturnType<typeof setTimeout>>()
    const itemRef = useRef(params.item)
    itemRef.current = params.item

    return (
      <Box sx={{ width: '100%', paddingTop: 2 }}>
        <TextField
          fullWidth
          label={label}
          defaultValue={params.item.value ?? ''}
          onChange={(event) => {
            clearTimeout(timerRef.current)
            const value = event.target.value
            timerRef.current = setTimeout(() => applyValue({ ...itemRef.current, value }), 300)
          }}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </Box>
    )
  },
  headerLabel: label,
})

export const TOKEN_COLUMNS: GridColDef<CardTokenRow>[] = [
  {
    field: 'walletProvider',
    headerName: 'Wallet',
    flex: 1,
    minWidth: 150,
    valueFormatter: (value) => formatTokenEnum(value),
    renderCell: (params) => <DataGridCellText>{formatTokenEnum(params.value)}</DataGridCellText>,
    filterOperators: [createSelectOperator('Wallet', Object.values(WalletProvider), formatTokenEnum)],
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    minWidth: 150,
    valueFormatter: (value) => formatTokenEnum(value),
    renderCell: (params) => <DataGridCellText>{formatTokenEnum(params.value)}</DataGridCellText>,
    filterOperators: [createSelectOperator('Status', Object.values(Status), formatTokenEnum)],
  },
  {
    field: 'walletProviderName',
    headerName: 'Name',
    flex: 1,
    valueFormatter: (value) => value || '-',
    renderCell: (params) => <DataGridCellText>{params.value || '-'}</DataGridCellText>,
    filterOperators: [createTextFilterOperator('Name')],
  },
  {
    field: 'deviceName',
    headerName: 'Device name',
    flex: 1,
    valueFormatter: (value) => value || '-',
    renderCell: (params) => <DataGridCellText>{params.value || '-'}</DataGridCellText>,
    filterOperators: [createTextFilterOperator('Device name')],
  },
  {
    field: 'deviceType',
    headerName: 'Device type',
    flex: 1,
    minWidth: 150,
    valueFormatter: (value) => formatTokenEnum(value),
    renderCell: (params) => <DataGridCellText>{formatTokenEnum(params.value)}</DataGridCellText>,
    filterOperators: [createSelectOperator('Device type', Object.values(DeviceType), formatTokenEnum)],
  },
  {
    field: 'mobileNumberLast4',
    headerName: 'Mobile (last 4)',
    flex: 1,
    valueFormatter: (value) => value || '-',
    renderCell: (params) => <DataGridCellText>{params.value || '-'}</DataGridCellText>,
    filterOperators: [createTextFilterOperator('Mobile (last 4)')],
  },
]
